<template>
  <div class="auth-page min-vh-100 d-flex position-relative">
    <!-- Top Floating Actions (Theme & Language) -->
    <div class="auth-floating-actions position-absolute top-0 end-0 p-4 z-index-3 d-flex align-items-center gap-2">
      <button
        type="button"
        class="auth-action-btn"
        :title="isDarkTheme ? 'İşıqlı rejim' : 'Qaranlıq rejim'"
        @click="onToggleTheme"
      >
        <i :class="isDarkTheme ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-fill text-primary'"></i>
      </button>
    </div>

    <!-- Main Auth Grid -->
    <div class="container-fluid p-0 d-flex flex-column flex-lg-row min-vh-100">
      <!-- Left Hero Panel (Visual Brand Side) -->
      <div class="auth-hero-panel col-lg-6 d-none d-lg-flex flex-column justify-content-between p-5 position-relative overflow-hidden">
        <div class="auth-hero-bg position-absolute top-0 start-0 w-100 h-100"></div>
        <div class="auth-hero-overlay position-absolute top-0 start-0 w-100 h-100"></div>

        <!-- Top Brand -->
        <div class="position-relative z-index-2">
          <img :src="logoWhite" alt="Progress IMS" class="auth-hero-logo" />
        </div>

        <!-- Center Value Proposition -->
        <div class="position-relative z-index-2 my-auto py-5">
          <span class="badge bg-gradient-info mb-3 px-3 py-2 text-xs font-weight-bold text-uppercase tracking-wider">
            Sığorta İdarəetmə Sistemi
          </span>
          <h1 class="text-white font-weight-bolder mb-3 display-5">
            Həyat Sığortası Portalı
          </h1>
          <p class="text-white opacity-8 text-sm lead mb-4" style="max-width: 480px;">
            Müqavilələr, sorğular, anderayting və sığorta hadisələrinin vahid rəqəmsal platforması.
          </p>

          <div class="auth-feature-list d-flex flex-column gap-3 mt-4">
            <div class="auth-feature-item d-flex align-items-center gap-3">
              <div class="auth-feature-icon">
                <i class="bi bi-shield-check"></i>
              </div>
              <div>
                <h6 class="text-white mb-0 text-sm font-weight-bold">Təhlükəsiz və Sürətli Giriş</h6>
                <span class="text-white opacity-7 text-xs">Məlumatların şifrələnməsi və etibarlı sessiya</span>
              </div>
            </div>

            <div class="auth-feature-item d-flex align-items-center gap-3">
              <div class="auth-feature-icon">
                <i class="bi bi-lightning-charge"></i>
              </div>
              <div>
                <h6 class="text-white mb-0 text-sm font-weight-bold">Avtomatlaşdırılmış İdarəetmə</h6>
                <span class="text-white opacity-7 text-xs">Müqavilə və təkliflərin operativ təsdiqi</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom Copyright -->
        <div class="position-relative z-index-2">
          <p class="text-white opacity-6 text-xs mb-0">
            © {{ currentYear }} Progress IMS. Bütün hüquqlar qorunur.
          </p>
        </div>
      </div>

      <!-- Right Form Panel -->
      <div class="auth-form-panel col-12 col-lg-6 d-flex align-items-center justify-content-center p-4 p-sm-5 position-relative">
        <div class="auth-card w-100" style="max-width: 440px;" v-loading="loading">
          <!-- Mobile Brand Logo -->
          <div class="d-lg-none text-center mb-4">
            <img :src="isDarkTheme ? logoWhite : logoMain" alt="Progress IMS" class="auth-mobile-logo" />
          </div>

          <!-- Header -->
          <div class="text-start mb-4">
            <h2 class="font-weight-bolder auth-title mb-1">
              Xoş gəlmisiniz! 👋
            </h2>
            <p class="text-sm auth-subtitle mb-0">
              Davam etmək üçün sistemə daxil olun
            </p>
          </div>

          <!-- Alert message -->
          <div v-if="message" class="alert alert-danger d-flex align-items-center gap-2 p-3 mb-4 border-radius-lg text-sm" role="alert">
            <i class="bi bi-exclamation-triangle-fill flex-shrink-0"></i>
            <div>{{ message }}</div>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleSignIn" role="form" class="auth-form">
            <!-- Username input -->
            <div class="form-group mb-3">
              <label class="form-label text-xs font-weight-bold mb-1">İstifadəçi adı və ya Email</label>
              <div class="input-group auth-input-group">
                <span class="input-group-text border-end-0">
                  <i class="bi bi-person-fill"></i>
                </span>
                <input
                  v-model="login"
                  type="text"
                  class="form-control border-start-0 ps-0"
                  placeholder="İstifadəçi adını daxil edin"
                  autocomplete="username"
                  required
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Password input -->
            <div class="form-group mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label text-xs font-weight-bold mb-0">Şifrə</label>
              </div>
              <div class="input-group auth-input-group">
                <span class="input-group-text border-end-0">
                  <i class="bi bi-lock-fill"></i>
                </span>
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control border-start-0 border-end-0 ps-0"
                  placeholder="Şifrəni daxil edin"
                  autocomplete="current-password"
                  required
                  :disabled="loading"
                />
                <button
                  type="button"
                  class="input-group-text bg-transparent border-start-0 cursor-pointer"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'bi bi-eye-slash-fill' : 'bi bi-eye-fill'"></i>
                </button>
              </div>
            </div>

            <!-- Remember Me & Forgot Password -->
            <div class="d-flex align-items-center justify-content-between mb-4">
              <div class="form-check form-switch ps-0">
                <input
                  class="form-check-input ms-0 me-2"
                  type="checkbox"
                  id="rememberMe"
                  v-model="rememberMe"
                />
                <label class="form-check-label text-xs" for="rememberMe">Məni xatırla</label>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="text-center">
              <button
                type="submit"
                class="btn bg-gradient-info w-100 py-3 mb-0 btn-login d-flex align-items-center justify-content-center gap-2"
                :disabled="loading"
              >
                <i v-if="loading" class="spinner-border spinner-border-sm" role="status"></i>
                <span class="font-weight-bold">{{ loading ? 'Daxil olunur...' : 'Daxil ol' }}</span>
                <i v-if="!loading" class="bi bi-arrow-right"></i>
              </button>
            </div>
          </form>

          <!-- Footer Info -->
          <div class="text-center mt-4">
            <small class="text-xs text-muted">
              Daxil olmaqda çətinlik çəkirsinizsə, administrator ilə əlaqə saxlayın.
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import logoMain from "@/assets/img/logos/progressico2.png";
import logoWhite from "@/assets/img/logos/progressico2white.png";
import { getTheme, toggleTheme } from "@/project/Source/theme";

export default {
  name: "SignIn",
  data() {
    return {
      login: "",
      password: "",
      showPassword: false,
      rememberMe: true,
      message: null,
      loading: false,
      logoMain,
      logoWhite,
      theme: getTheme(),
    };
  },
  computed: {
    isDarkTheme() {
      return this.theme === "dark";
    },
    currentYear() {
      return new Date().getFullYear();
    },
  },
  methods: {
    onToggleTheme() {
      this.theme = toggleTheme();
    },
    async handleSignIn() {
      this.message = null;

      const userTrim = (this.login || "").trim();
      if (!userTrim) {
        this.message = "İstifadəçi adını daxil edin.";
        return;
      }
      if (!this.password) {
        this.message = "Şifrəni daxil edin.";
        return;
      }

      this.loading = true;

      // Mock or standard auth payload handler
      setTimeout(() => {
        try {
          const userObj = {
            userId: 1,
            userName: userTrim,
            fullName: userTrim,
            companyName: "Progress Life Insurance",
            companyId: 1,
            authenticated: true,
            locked: false,
          };

          const rolesObj = [{ roleId: 1, name: "Admin" }];
          const jwtToken = "jwt_" + Date.now().toString(36);

          window.localStorage.setItem(
            "jwt",
            CryptoJS.AES.encrypt(JSON.stringify(jwtToken), "secret_key").toString()
          );
          window.localStorage.setItem(
            "roles",
            CryptoJS.AES.encrypt(JSON.stringify(rolesObj), "secret_key").toString()
          );
          window.localStorage.setItem(
            "user",
            CryptoJS.AES.encrypt(JSON.stringify(userObj), "secret_key").toString()
          );

          // Handle redirect
          const returnParam = this.$route.params.return;
          if (returnParam) {
            this.$router.push(`/${returnParam}`);
          } else if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          } else {
            this.$router.push("/home");
          }
        } catch (err) {
          this.message = "Giriş zamanı xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.";
        } finally {
          this.loading = false;
        }
      }, 500);
    },
  },
};
</script>

<style scoped lang="scss">
.auth-page {
  background-color: var(--app-surface, #ffffff);
  color: var(--app-heading, #1e293b);
  overflow-x: hidden;
}

.auth-floating-actions {
  z-index: 10;
}

.auth-action-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--app-surface-elevated, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: var(--app-heading, #1e293b);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
}

/* Left Hero Panel */
.auth-hero-panel {
  min-height: 100vh;
  background: #0f172a;
}

.auth-hero-bg {
  background-image: url("~@/assets/img/curved-images/curved6.jpg");
  background-size: cover;
  background-position: center;
  filter: brightness(0.65) saturate(1.2);
}

.auth-hero-overlay {
  background: linear-gradient(
    135deg,
    rgba(11, 79, 156, 0.88) 0%,
    rgba(15, 23, 42, 0.95) 100%
  );
}

.auth-hero-logo {
  height: 42px;
  object-fit: contain;
}

.auth-feature-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}

/* Right Form Panel */
.auth-form-panel {
  min-height: 100vh;
  background-color: var(--app-surface, #ffffff);
}

.auth-mobile-logo {
  height: 40px;
  object-fit: contain;
}

.auth-title {
  color: var(--app-heading, #0f172a);
  font-size: 1.75rem;
}

.auth-subtitle {
  color: var(--app-text, #64748b);
}

.auth-input-group {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--app-border, #cbd5e1);
  transition: all 0.2s ease;

  .input-group-text {
    background-color: transparent;
    color: var(--app-text, #94a3b8);
    border: none;
    padding-left: 14px;
    padding-right: 10px;
  }

  .form-control {
    border: none;
    background-color: transparent;
    color: var(--app-heading, #0f172a);
    padding: 12px 14px 12px 0;
    font-size: 0.925rem;

    &:focus {
      box-shadow: none;
    }
  }

  &:focus-within {
    border-color: var(--bs-info, #0b4f9c);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--bs-info, #0b4f9c) 15%, transparent);

    .input-group-text {
      color: var(--bs-info, #0b4f9c);
    }
  }
}

.btn-login {
  border-radius: 10px;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 14px rgba(11, 79, 156, 0.3);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(11, 79, 156, 0.4);
  }
}

/* Dark mode overrides */
html[data-theme="dark"] {
  .auth-form-panel {
    background-color: #111827;
  }

  .auth-title {
    color: #f8fafc;
  }

  .auth-subtitle {
    color: #94a3b8;
  }

  .auth-input-group {
    border-color: #334155;
    background-color: #1e293b;

    .form-control {
      color: #f8fafc;
    }
  }
}
</style>
